/*
  Case study components.

  <case-study-compare>: before and after slider. One function (setPos) keeps the range value,
  the divider position, the spoken value and the three view buttons in step. Strings come
  from data attributes rendered with the theme's translations.

  <case-study-toggle>: shows or hides the full-length screenshots in a pair or pair grid. The
  button keeps its place on screen when the group collapses, and the sticky header is told
  not to slide back over it.
*/

if (!customElements.get('case-study-compare')) {
  customElements.define(
    'case-study-compare',
    class CaseStudyCompare extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.range = this.querySelector('[data-compare-range]');
        this.buttons = Array.from(this.querySelectorAll('[data-compare-set]'));
        if (!this.range) return;

        this.range.addEventListener('input', () => this.setPos(this.range.value), { signal });
        this.buttons.forEach((button) => {
          button.addEventListener('click', () => this.setPos(button.dataset.compareSet), { signal });
        });
        this.setPos(this.range.value);
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
      }

      setPos(value) {
        const pos = Math.max(0, Math.min(100, Math.round(Number(value))));
        this.style.setProperty('--pos', `${pos}%`);
        this.range.value = String(pos);

        let text = this.dataset.valueSplit || '';
        if (pos === 100) text = this.dataset.valueBeforeOnly || text;
        else if (pos === 0) text = this.dataset.valueAfterOnly || text;
        else text = text.replace('[before]', String(pos)).replace('[after]', String(100 - pos));
        this.range.setAttribute('aria-valuetext', text);

        this.buttons.forEach((button) => {
          button.setAttribute('aria-pressed', String(Number(button.dataset.compareSet) === pos));
        });
      }
    }
  );
}

if (!customElements.get('case-study-toggle')) {
  customElements.define(
    'case-study-toggle',
    class CaseStudyToggle extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.button = this.querySelector('[data-toggle-button]');
        if (!this.button) return;

        this.button.addEventListener('click', () => this.toggle(), { signal });
        // In the theme editor, open the group when one of the section's blocks is selected.
        // Listen on the section root: a pair's step list sits outside this element.
        const root = this.closest('.case-study') || this;
        root.addEventListener('shopify:block:select', () => this.setOpen(true), { signal });
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
      }

      toggle() {
        this.setOpen(this.dataset.open !== 'true');
      }

      setOpen(open) {
        const wasOpen = this.dataset.open === 'true';
        if (open === wasOpen) return;
        const before = this.button.getBoundingClientRect().top;
        this.dataset.open = String(open);
        this.button.setAttribute('aria-expanded', String(open));
        if (!open) {
          const after = this.button.getBoundingClientRect().top;
          const header = document.querySelector('sticky-header');
          if (header) header.dispatchEvent(new Event('preventHeaderReveal'));
          window.scrollBy({ top: after - before, behavior: 'instant' });
        }
      }
    }
  );
}
